/** PDF 文本层：在 page.getTextContent() 结果中定位 quote 并计算高亮矩形（viewport 坐标） */

import type { PDFDocumentProxy } from 'pdfjs-dist'

export type PdfTextItem = {
  str: string
  transform: number[]
  width: number
  height: number
  hasEOL?: boolean
}

export type PdfHighlightRect = {
  left: number
  top: number
  width: number
  height: number
}

type TextSpan = {
  itemIdx: number
  start: number
  end: number
}

type BuiltText = {
  text: string
  spans: TextSpan[]
  items: PdfTextItem[]
}

const MIN_CANDIDATE_LEN = 4
const MAX_CANDIDATES = 12
const PAGE_SEARCH_AROUND = 40

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

export function normalizeForMatch(text: string): string {
  return (text || '')
    .normalize('NFKC')
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .replace(/[\t\f\v]+/g, ' ')
    .replace(/\n+/g, '\n')
    .trim()
}

export function normalizeDigitsAndSymbols(text: string): string {
  let s = normalizeForMatch(text)
  s = s.replace(/(?<=\d)[,，](?=\d)/g, '')
  s = s.replace(/[×Ｘ＊]/g, 'x')
  return s.replace(/\s+/g, '')
}

/** 与后端 quote_match.build_quote_match_candidates 同思路的通用候选集 */
export function buildQuoteMatchCandidates(quote: string | null | undefined): string[] {
  const raw = (quote || '').replace(/\s+/g, ' ').trim()
  if (!raw) return []

  const out: string[] = []
  const seen = new Set<string>()
  const add = (s: string) => {
    const t = s.trim()
    if (t.length < MIN_CANDIDATE_LEN || seen.has(t)) return
    seen.add(t)
    out.push(t)
  }

  add(raw)
  const norm = normalizeForMatch(raw)
  add(norm)
  const compact = norm.replace(/\s+/g, '')
  add(compact)
  add(normalizeDigitsAndSymbols(raw))

  for (const part of raw.split(/[。；;！？!?.\n]+/)) {
    add(part.trim())
  }
  for (const part of raw.split(/[,，、：:]+/)) {
    if (part.trim().length >= MIN_CANDIDATE_LEN) add(part.trim())
  }

  if (raw.length > 80) {
    add(raw.slice(0, 80))
    add(norm.split(/[。；;.\n]/)[0]?.trim() || '')
  }
  if (raw.length > 24) add(raw.slice(0, 24))
  if (compact.length > 40) add(compact.slice(0, 40))

  return out.slice(0, MAX_CANDIDATES)
}

/** @deprecated 使用 buildQuoteMatchCandidates */
export function quoteSearchCandidates(quote: string | null | undefined): string[] {
  return buildQuoteMatchCandidates(quote)
}

function buildPageText(items: PdfTextItem[]): BuiltText {
  let text = ''
  const spans: TextSpan[] = []
  for (let i = 0; i < items.length; i++) {
    const str = items[i]?.str ?? ''
    if (!str) continue
    const start = text.length
    text += str
    spans.push({ itemIdx: i, start, end: text.length })
    if (items[i]?.hasEOL) text += '\n'
  }
  return { text, spans, items }
}

function flexibleWhitespaceMatch(text: string, quote: string): [number, number] | null {
  const parts = quote.trim().split(/\s+/).filter(Boolean)
  if (parts.length < 2) return null
  const pattern = parts.map(escapeRegex).join('\\s*')
  const m = new RegExp(pattern, 's').exec(text)
  if (m) return [m.index, m.index + m[0].length]
  return null
}

function mapCompactRange(text: string, cStart: number, cEnd: number): [number, number] | null {
  let ci = 0
  let oStart: number | null = null
  let oEnd: number | null = null
  for (let oi = 0; oi < text.length; oi++) {
    if (!/\s/.test(text[oi]!)) {
      if (ci === cStart) oStart = oi
      if (ci === cEnd) {
        oEnd = oi
        break
      }
      ci++
    }
  }
  if (oStart == null) return null
  if (oEnd == null) oEnd = text.length
  return [oStart, oEnd]
}

function compactMatch(text: string, quote: string): [number, number] | null {
  const normText = normalizeForMatch(text)
  const normQuote = normalizeForMatch(quote)
  const compactText = normText.replace(/\s+/g, '')
  const compactQuote = normQuote.replace(/\s+/g, '')
  if (compactQuote.length < 8) return null

  let pos = compactText.indexOf(compactQuote)
  if (pos >= 0) return mapCompactRange(text, pos, pos + compactQuote.length)

  const digitText = normalizeDigitsAndSymbols(text)
  const digitQuote = normalizeDigitsAndSymbols(quote)
  if (digitQuote.length >= 8) {
    pos = digitText.indexOf(digitQuote)
    if (pos >= 0) return mapCompactRange(text, pos, pos + digitQuote.length)
  }

  const prefix = compactQuote.slice(0, Math.min(24, compactQuote.length))
  pos = compactText.indexOf(prefix)
  if (pos >= 0) {
    return mapCompactRange(text, pos, pos + Math.min(compactQuote.length, prefix.length + 20))
  }
  return null
}

export function findQuoteRange(text: string, quote: string): [number, number] | null {
  const q = quote.trim()
  if (!q || !text) return null

  let pos = text.indexOf(q)
  if (pos >= 0) return [pos, pos + q.length]

  const flex = flexibleWhitespaceMatch(text, q)
  if (flex) return flex

  const compact = compactMatch(text, q)
  if (compact) return compact

  const normQ = normalizeForMatch(q)
  if (normQ && normQ !== q) {
    const normFlex = flexibleWhitespaceMatch(text, normQ)
    if (normFlex) return normFlex
    const normCompact = compactMatch(text, normQ)
    if (normCompact) return normCompact
  }

  if (q.length > 12) {
    pos = text.indexOf(q.slice(0, 12))
    if (pos >= 0) return [pos, pos + Math.min(q.length, 40)]
  }
  return null
}

function itemToRect(
  item: PdfTextItem,
  localStart: number,
  localEnd: number,
  viewportTransform: number[],
): PdfHighlightRect {
  const Util = {
    transform: (m1: number[], m2: number[]) => [
      m1[0]! * m2[0]! + m1[2]! * m2[1]!,
      m1[1]! * m2[0]! + m1[3]! * m2[1]!,
      m1[0]! * m2[2]! + m1[2]! * m2[3]!,
      m1[1]! * m2[2]! + m1[3]! * m2[3]!,
      m1[0]! * m2[4]! + m1[2]! * m2[5]! + m1[4]!,
      m1[1]! * m2[4]! + m1[3]! * m2[5]! + m1[5]!,
    ],
  }
  const tx = Util.transform(viewportTransform, item.transform)
  const matrixHeight = Math.hypot(tx[2]!, tx[3]!)
  const viewportScale = Math.hypot(viewportTransform[0]!, viewportTransform[1]!)
  const itemHeight =
    item.height > 0 ? item.height * viewportScale : matrixHeight
  const fontHeight = Math.min(
    matrixHeight > 0 ? matrixHeight : itemHeight,
    itemHeight > 0 ? itemHeight * 1.08 : matrixHeight,
    28,
  )
  const totalWidth = item.width || 0
  const strLen = Math.max(item.str.length, 1)
  const startRatio = localStart / strLen
  const endRatio = localEnd / strLen
  const width = Math.max(totalWidth * (endRatio - startRatio), 2)
  const left = tx[4]! + totalWidth * startRatio
  const top = tx[5]! - fontHeight
  const height = Math.min(Math.max(fontHeight * 1.05, 8), 24)
  return { left, top, width, height }
}

export function rangeToHighlightRects(
  built: BuiltText,
  start: number,
  end: number,
  viewportTransform: number[],
): PdfHighlightRect[] {
  const rects: PdfHighlightRect[] = []
  for (const span of built.spans) {
    if (span.end <= start || span.start >= end) continue
    const item = built.items[span.itemIdx]
    if (!item) continue
    const localStart = Math.max(0, start - span.start)
    const localEnd = Math.min(item.str.length, end - span.start)
    if (localEnd <= localStart) continue
    rects.push(itemToRect(item, localStart, localEnd, viewportTransform))
  }
  return mergeLineRects(rects)
}

function mergeLineRects(rects: PdfHighlightRect[]): PdfHighlightRect[] {
  if (!rects.length) return []
  const sorted = [...rects].sort((a, b) => a.top - b.top || a.left - b.left)
  const out: PdfHighlightRect[] = []
  for (const r of sorted) {
    const last = out[out.length - 1]
    const sameLine =
      last != null &&
      Math.abs(last.top - r.top) < Math.min(last.height, r.height) * 0.45
    const adjacent =
      last != null && r.left <= last.left + last.width + Math.max(8, last.height * 0.35)
    if (last && sameLine && adjacent) {
      const right = Math.max(last.left + last.width, r.left + r.width)
      last.width = right - last.left
      last.height = Math.min(last.height, r.height)
    } else {
      out.push({ ...r })
    }
  }
  return out
}

function highlightTightnessScore(rects: PdfHighlightRect[]): number {
  if (!rects.length) return Number.POSITIVE_INFINITY
  const area = rects.reduce((sum, r) => sum + r.width * r.height, 0)
  const maxWidth = Math.max(...rects.map((r) => r.width))
  return area + maxWidth * 0.35
}

export function scaleHighlightRects(
  rects: PdfHighlightRect[],
  scaleX: number,
  scaleY: number,
): PdfHighlightRect[] {
  if (Math.abs(scaleX - 1) < 0.01 && Math.abs(scaleY - 1) < 0.01) return rects
  return rects.map((r) => ({
    left: r.left * scaleX,
    top: r.top * scaleY,
    width: r.width * scaleX,
    height: r.height * scaleY,
  }))
}

export function findHighlightsOnPage(
  items: PdfTextItem[],
  quote: string,
  viewportTransform: number[],
): PdfHighlightRect[] {
  const built = buildPageText(items)
  let best: PdfHighlightRect[] | null = null
  let bestScore = Number.POSITIVE_INFINITY
  for (const cand of buildQuoteMatchCandidates(quote)) {
    const range = findQuoteRange(built.text, cand)
    if (!range) continue
    const rects = rangeToHighlightRects(built, range[0], range[1], viewportTransform)
    if (!rects.length) continue
    const score = highlightTightnessScore(rects)
    if (score < bestScore) {
      bestScore = score
      best = rects
    }
  }
  return best || []
}

export function pageSearchOrder(
  startPage: number | null,
  numPages: number,
  maxAround = PAGE_SEARCH_AROUND,
): number[] {
  const start = startPage != null && startPage > 0 ? Math.min(startPage, numPages) : 1
  const order: number[] = []
  const seen = new Set<number>()
  const push = (p: number) => {
    if (p >= 1 && p <= numPages && !seen.has(p)) {
      seen.add(p)
      order.push(p)
    }
  }
  push(start)
  for (let d = 1; d <= numPages; d++) {
    push(start - d)
    push(start + d)
    if (order.length >= maxAround) break
  }
  return order
}

/** 先搜 hint 附近，再搜剩余页（通用全文兜底） */
export function fullPdfPageSearchOrder(startPage: number | null, numPages: number): number[] {
  const first = pageSearchOrder(startPage, numPages)
  const seen = new Set(first)
  const rest: number[] = []
  for (let p = 1; p <= numPages; p++) {
    if (!seen.has(p)) rest.push(p)
  }
  return [...first, ...rest]
}

/** 在 PDF 中定位引用所在页（仅文本检索，不渲染 canvas） */
export async function locateQuoteInPdf(
  pdf: PDFDocumentProxy,
  quote: string,
  hintPage: number | null | undefined,
  scale: number,
): Promise<{ pageNumber: number; highlights: PdfHighlightRect[]; matched: boolean }> {
  const q = quote.trim()
  const fallbackPage = hintPage && hintPage > 0 ? Math.min(hintPage, pdf.numPages) : 1
  if (!q) {
    return { pageNumber: fallbackPage, highlights: [], matched: false }
  }
  const candidates = buildQuoteMatchCandidates(q)
  for (const p of fullPdfPageSearchOrder(hintPage ?? null, pdf.numPages)) {
    const page = await pdf.getPage(p)
    const viewport = page.getViewport({ scale })
    const textContent = await page.getTextContent()
    const items = textContent.items as PdfTextItem[]
    for (const cand of candidates) {
      const highlights = findHighlightsOnPage(items, cand, viewport.transform)
      if (highlights.length > 0) {
        return { pageNumber: p, highlights, matched: true }
      }
    }
  }
  return { pageNumber: fallbackPage, highlights: [], matched: false }
}
